// DISCLAIMER
// Copyright 2026 ArangoDB GmbH, Cologne, Germany

import React from "react";
import { Button, Label, Modal, Table } from "semantic-ui-react";
import { ChartValueSource } from "../../api/charts/v1/icharts";
import { decodeValuesObject, flattenValues, redundantHint, SourceLabel, SourceLegend } from "./utils";

interface ValuesModalArgs {
  name?: string;
  values?: string;
  valueSources?: { [path: string]: ChartValueSource };
  redundantPaths?: string[];
  // The layers whose labels are explained, template and configuration when not set.
  sources?: string[];
  onClose: () => void;
}

// ValuesModal shows the Helm values of a chart, one row per value path, with the layer each value comes from.
const ValuesModal = ({ name, values, valueSources, redundantPaths, sources, onClose }: ValuesModalArgs) => {
  const flat = flattenValues(decodeValuesObject(values));
  const redundant = new Set(redundantPaths || []);
  return (
    <Modal open onClose={onClose} size="large">
      <Modal.Header>{name} values</Modal.Header>
      <Modal.Content scrolling>
        {Object.keys(flat).length === 0 ? (
          "No values."
        ) : (
          <>
            <SourceLegend sources={sources || ["template", "configuration"]} />
            <Table compact="very" size="small">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Path</Table.HeaderCell>
                  <Table.HeaderCell>Value</Table.HeaderCell>
                  <Table.HeaderCell>Source</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {Object.keys(flat)
                  .sort()
                  .map((path) => (
                    <Table.Row key={path}>
                      <Table.Cell style={{ fontFamily: "monospace" }}>{path}</Table.Cell>
                      <Table.Cell style={{ fontFamily: "monospace", wordBreak: "break-all" }}>{JSON.stringify(flat[path])}</Table.Cell>
                      <Table.Cell>
                        <SourceLabel source={valueSources && valueSources[path]} />
                        {redundant.has(path) && (
                          <Label size="tiny" basic color="orange" title={redundantHint}>
                            same as template
                          </Label>
                        )}
                      </Table.Cell>
                    </Table.Row>
                  ))}
              </Table.Body>
            </Table>
          </>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>Close</Button>
      </Modal.Actions>
    </Modal>
  );
};

export default ValuesModal;
